import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Prescription Branding API — Theme & template configuration
 *
 * Endpoints:
 *   GET  /api/organisation/prescription-config          — Get config
 *   PUT  /api/organisation/prescription-config          — Update config
 *   GET  /api/organisation/prescription-themes          — List themes
 *   POST /api/organisation/prescription-logo            — Upload logo
 *   POST /api/organisation/prescription-template-html   — Upload custom template
 *   POST /api/organisation/prescription-template-preview — Preview with sample data
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Get the current prescription branding config for the org.
 */
export const getPrescriptionConfig = async () => {
  const response = await apiClient.get('/api/organisation/prescription-config');
  return response.data;
};

/**
 * Update the prescription branding config.
 * @param {Object} config - PrescriptionBrandingConfig fields
 */
export const updatePrescriptionConfig = async (config) => {
  const response = await apiClient.put('/api/organisation/prescription-config', config);
  return response.data;
};

/**
 * List available prescription themes with descriptions.
 */
export const getPrescriptionThemes = async () => {
  const response = await apiClient.get('/api/organisation/prescription-themes');
  return response.data;
};

/**
 * Upload a clinic logo for prescriptions.
 * Returns { s3_key, message } — NOT a URL. Use getLogoPreviewUrl() for display.
 * @param {File} file - Image file (PNG/JPG, max 2MB)
 */
export const uploadPrescriptionLogo = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/api/organisation/prescription-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Get the logo image as a blob URL for display in the frontend.
 * Streams through our backend proxy — no S3 URL exposed.
 * @returns {Promise<string|null>} Object URL for <img src>, or null if no logo
 */
export const getLogoPreviewUrl = async () => {
  try {
    const response = await apiClient.get('/api/organisation/prescription-logo', {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  } catch (err) {
    // 404 = no logo configured; 500 = stale S3 key or storage error — both mean no logo to show
    const status = err.response?.status;
    if (status === 404 || status === 500) return null;
    throw err;
  }
};

/**
 * Upload a custom HTML (Jinja2) prescription template.
 * @param {File} file - HTML template file (max 500KB)
 */
export const uploadCustomTemplate = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/api/organisation/prescription-template-html', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Upload a letterhead image (PNG/JPG scan of printed prescription pad).
 * Backend base64-encodes the image, generates a Jinja2 HTML template, and stores both in S3.
 * Does NOT update org config — call updatePrescriptionConfig() on Save Branding.
 * @param {File} file - PNG or JPG image (max 5MB)
 * @returns {{ custom_template_s3_key, letterhead_raw_s3_key, top_offset_mm, message }}
 */
export const uploadPrescriptionLetterhead = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/api/organisation/prescription-letterhead', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000, // 3 min — AI pipeline (Extract→Generate→Screenshot→Refine) takes ~50s
  });
  return response.data;
};

/**
 * Adjust the data-overlay position on an already-uploaded PNG letterhead.
 * Re-generates the HTML in S3 with the new offsets and invalidates the preview cache.
 * Does NOT update org config.
 * @param {number} topMm - Distance from top of page in mm
 * @param {number} [leftMm=0] - Distance from left of page in mm
 * @returns {{ custom_template_s3_key, top_offset_mm, left_offset_mm, message }}
 */
export const updateLetterheadOffset = async (topMm, leftMm = 0) => {
  const response = await apiClient.post('/api/organisation/prescription-letterhead-offset', {
    letterhead_top_offset_mm: topMm,
    letterhead_left_offset_mm: leftMm,
  });
  return response.data;
};

/**
 * Upload a PDF letterhead for vector-quality prescription overlay.
 * Backend stores the PDF in S3. Does NOT update org config.
 * @param {File} file - PDF file (max 5MB)
 * @returns {{ letterhead_pdf_s3_key, top_offset_mm, left_offset_mm, message }}
 */
export const uploadPrescriptionLetterheadPdf = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/api/organisation/prescription-letterhead-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Get a PDF preview of the letterhead overlaid with sample prescription data.
 * Returns a blob URL for display in an iframe or <object> tag.
 * @param {string} letterheadPdfS3Key
 * @param {number} topOffsetMm
 * @param {number} leftOffsetMm
 * @param {number} rightOffsetMm
 * @returns {Promise<string>} blob URL (caller must URL.revokeObjectURL when done)
 */
export const getLetterheadPdfPreview = async (letterheadPdfS3Key, topOffsetMm, leftOffsetMm, rightOffsetMm) => {
  const response = await apiClient.post('/api/organisation/prescription-letterhead-pdf-preview', {
    letterhead_pdf_s3_key: letterheadPdfS3Key,
    top_offset_mm: topOffsetMm,
    left_offset_mm: leftOffsetMm,
    right_offset_mm: rightOffsetMm,
  }, { responseType: 'blob' });
  return URL.createObjectURL(response.data);
};

/**
 * Apply an edited HTML string as the custom template.
 * Converts the string to a File and uploads via the existing template-html endpoint,
 * which validates Jinja2 syntax and required variables before storing in S3.
 * @param {string} htmlString - Raw Jinja2 HTML template
 * @returns {{ s3_key, message }}
 */
export const applyLetterheadSource = async (htmlString) => {
  const file = new File([htmlString], 'custom_template.html', { type: 'text/html' });
  return uploadCustomTemplate(file);
};

/**
 * Get a preview HTML of a theme rendered with sample data.
 * @param {string} [theme] - Theme name (classic/modern/compact/custom)
 * @param {string} [s3Key] - Staged custom_template_s3_key (used before Save Branding commits it)
 */
export const getTemplatePreview = async (theme, s3Key) => {
  const qs = new URLSearchParams();
  if (theme) qs.set('theme', theme);
  if (s3Key) qs.set('s3_key', s3Key);
  const params = qs.toString() ? `?${qs.toString()}` : '';
  const response = await apiClient.post(
    `/api/organisation/prescription-template-preview${params}`
  );
  return response.data;
};

export const getVariantPreview = async (variantId) => {
  const qs = new URLSearchParams({ theme: 'custom', variant_id: variantId });
  const response = await apiClient.post(
    `/api/organisation/prescription-template-preview?${qs.toString()}`
  );
  return response.data;
};
